import { useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Step from '@mui/material/Step'
import StepButton from '@mui/material/StepButton'
import Stepper from '@mui/material/Stepper'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

import { Consultation } from '@/core/models/consultation.model'
import { DeviceExamsSection, MedicalVideoPanel, StethoscopePanel, useHasConfiguredExamDevice } from '@/pages/diagnostic/containers/AllInOneDiagnostics'
import Biologicals from '@/pages/diagnostic/containers/Biologicals'
import Documents from '@/pages/diagnostic/containers/Documents'
import PC300Mesures from '@/pages/diagnostic/containers/PC300Mesures'
import RpiMesures from '@/pages/diagnostic/containers/RpiMesures'
import RpiProvider from '@/providers/rpi/RpiProvider'
import { getEnv } from '@/utils/helpers'
import { ReceptionIdentityForm } from './ReceptionIdentityForm'

type ReceptionConsultationStepsProps = {
  consultation: Consultation
  consultationId?: string
  doctorName?: string
  isDoctorOnline: boolean
  isStreamer: boolean
  rpiMeetingId?: string
}

/**
 * The live consultation as the simplified reception sees it, in place of the 9-entry menu:
 * ① identity, ② vital signs, ③ examination: doctor status + the examination tools the operator runs during the call
 * (medical camera, stethoscope, ECG and spirometry, biological tests, files). The call itself stays in the right pane.
 */
export function ReceptionConsultationSteps(props: ReceptionConsultationStepsProps) {
  const { consultation, consultationId, doctorName, isDoctorOnline, isStreamer, rpiMeetingId } = props
  useLingui()
  const [activeStep, setActiveStep] = useState(0)
  const steps = [t`Identity`, t`Vital signs`, t`Examination`]

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: 3 }}>
      <Stepper nonLinear activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label, index) => (
          <Step key={label} completed={index < activeStep}>
            <StepButton onClick={() => setActiveStep(index)}>{label}</StepButton>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && <ReceptionIdentityForm patient={consultation.patient} onSaved={() => setActiveStep(1)} />}

      {activeStep === 1 && (
        <Stack spacing={2}>
          {rpiMeetingId ? (
            <RpiProvider isStreamer={isStreamer} meetingID={rpiMeetingId} serverUrl={getEnv('VITE_RPI_GATEWAY')!}>
              <RpiMesures isStreamer={isStreamer} consultationId={consultationId} />
            </RpiProvider>
          ) : (
            <PC300Mesures consultationId={consultationId} />
          )}
          <Button variant="contained" size="large" onClick={() => setActiveStep(2)}>
            <Trans>Next: examination</Trans>
          </Button>
        </Stack>
      )}

      {activeStep === 2 && (
        <Stack spacing={3}>
          {isDoctorOnline ? (
            <Alert severity="success" variant="filled">
              <Trans>Dr. {doctorName} has joined the consultation.</Trans>
            </Alert>
          ) : (
            <Alert severity="info">
              <Trans>Waiting for a doctor. The patient stays in the waiting room; the call will start on the right as soon as a doctor accepts.</Trans>
            </Alert>
          )}
          <DoctorStepTools consultationId={consultationId} isStreamer={isStreamer} />
        </Stack>
      )}
    </Box>
  )
}

type DoctorTool = 'medicalVideo' | 'stethoscope' | 'ecg' | 'biologicals' | 'files'

/** The tools the operator uses while the doctor examines the patient, as in the usual consultation. */
function DoctorStepTools({ consultationId, isStreamer }: { consultationId?: string; isStreamer: boolean }) {
  useLingui()
  const [tool, setTool] = useState<DoctorTool>('medicalVideo')
  // Only when an ECG / spirometry device is configured on this workstation.
  const hasExamDevice = useHasConfiguredExamDevice()

  return (
    <Box>
      <Tabs value={tool} onChange={(_event, value: DoctorTool) => setTool(value)} variant="fullWidth" sx={{ mb: 2 }}>
        <Tab value="medicalVideo" label={t`Medical camera`} />
        <Tab value="stethoscope" label={t`Stethoscope`} />
        {hasExamDevice && <Tab value="ecg" label={t`ECG and spirometry`} />}
        <Tab value="biologicals" label={t`Biological tests`} />
        <Tab value="files" label={t`Files`} />
      </Tabs>
      {tool === 'medicalVideo' && (
        <MedicalVideoPanel consultationId={consultationId} isStreamer={isStreamer} videoHeight="auto" fitVideoToStream maxVideoHeight="50vh" />
      )}
      {tool === 'stethoscope' && <StethoscopePanel consultationId={consultationId} isStreamer={isStreamer} />}
      {tool === 'ecg' && hasExamDevice && (
        <DeviceExamsSection consultationId={consultationId} biometricsHint={t`Update height and weight in the vital signs tab for more accurate device data.`} />
      )}
      {tool === 'biologicals' && <Biologicals consultationId={consultationId} />}
      {tool === 'files' && <Documents consultationId={consultationId} />}
    </Box>
  )
}
